import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Edit2, CheckCircle } from "lucide-react";
import ShareMenu from "./ShareMenu";
import { avatarUrl } from "../lib/avatar";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";

export default function StrategyCard({ s, onLike, onShare, compact = false }) {
  const { user } = useAuth();
  const [localSucceeded, setLocalSucceeded] = useState(s.succeeded || false);
  const [localShares, setLocalShares] = useState(s.shares || 0);
  const isAuthor = user?.id === s.author?.id;

  const toggleSucceeded = async () => {
    const nextVal = !localSucceeded;
    setLocalSucceeded(nextVal);
    await supabase.from("strategies").update({ succeeded: nextVal }).eq("id", s.id);
  };

  const handleShare = async () => {
    if (!user) return;
    // Attempt to insert into strategy_shares
    const { error } = await supabase.from("strategy_shares").insert({
      strategy_id: s.id,
      user_id: user.id
    });
    
    // If no error, it's a new share for this user. Increment locally!
    if (!error) {
      setLocalShares(prev => prev + 1);
    }
    
    // Still call the parent onShare if needed
    onShare?.(s);
  };

  return (
    <article
      className="brutal-card p-5 sm:p-6 relative"
      data-testid={`strategy-card-${s.id}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <Link to={`/u/${s.author?.id}`} className="shrink-0" data-testid={`author-link-${s.id}`}>
          <img
            src={avatarUrl(s.author?.avatar_url, s.author?.id)}
            alt={s.author?.name}
            className="w-12 h-12 border-2 border-ink shadow-brutal-sm bg-cream blob"
          />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-display font-extrabold uppercase tracking-tight">{s.author?.name}</span>
            <span className="font-mono text-xs text-ink/60">{s.author?.participant_id}</span>
          </div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-ink/60">
            {new Date(s.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-2">
        {localSucceeded && (
          <span className="bg-[#FFD600] text-ink font-display font-extrabold uppercase px-2 py-0.5 text-xs tracking-widest border-2 border-ink shadow-brutal-sm rotate-[-2deg]">
            Succeeded
          </span>
        )}
        <Link to={`/s/${s.id}`} data-testid={`strategy-title-${s.id}`}>
          <h3 className={`font-display font-extrabold tracking-tight ${compact ? "text-xl" : "text-2xl sm:text-3xl"} leading-tight`}>
            {s.title}
          </h3>
        </Link>
      </div>

      {!compact && (
        <p className="text-ink/80 leading-relaxed mb-4">{s.summary}</p>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        {(s.tags || []).slice(0, 5).map((t) => (
          <Link to={`/strategies?tag=${t}`} key={t} className="tag-pill" data-testid={`tag-${t}`}>
            #{t}
          </Link>
        ))}
        {s.goal_amount > 0 && (
          <span className="tag-pill" style={{ background: "#2B5CFF", color: "#fff" }}>
            Target ${Number(s.goal_amount).toLocaleString()}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between pt-3 border-t-2 border-ink">
        <div className="flex items-center gap-2">
          <button
            onClick={() => onLike?.(s)}
            className={`brutal-btn text-xs ${s.liked_by_me ? "brutal-btn-red" : ""}`}
            data-testid={`like-btn-${s.id}`}
          >
            <Heart size={14} fill={s.liked_by_me ? "currentColor" : "none"} />
            {s.like_count || 0}
          </button>
          <Link to={`/s/${s.id}`} className="brutal-btn text-xs" data-testid={`comment-btn-${s.id}`}>
            <MessageCircle size={14} /> {s.comment_count || 0}
          </Link>
          <ShareMenu
            url={`/s/${s.id}`}
            title={s.title}
            count={localShares}
            onShare={handleShare}
            compact
          />
        </div>
        
        {isAuthor && (
          <div className="flex items-center gap-2">
            <button 
              onClick={toggleSucceeded}
              className={`brutal-btn text-xs ${localSucceeded ? "brutal-btn-yellow" : ""}`}
              title={localSucceeded ? "Mark as incomplete" : "Mark as succeeded"}
            >
              <CheckCircle size={14} />
            </button>
            <Link to={`/s/${s.id}/edit`} className="brutal-btn text-xs" title="Edit Strategy">
              <Edit2 size={14} />
            </Link>
          </div>
        )}
      </div>
    </article>
  );
}
